import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import BreadCrumb from "@/components/Blog/BreadCrumb";
import RenderDelivery from "@/components/FastDelivery/RenderDelivery";
import SEO from "@/components/seo";
import { fastDeliveryDisplayTypes } from "@/constants/FastDelivery";
import { buildCanonicalUrl } from "@/utils/canonical";

const FastDelivery = ({ deliveryData, metadata, locale }) => {
  const ArrayOfBreadCrumb = [
    {
      name_EN: "Delivery",
      name_AR: "التوصيل",
      url_key: "/",
    },
  ];

  return (
    <div>
      <SEO title={metadata?.title} description={metadata?.description}>
        <link
          rel="canonical"
          href={buildCanonicalUrl("/fast-delivery", locale)}
        />
      </SEO>
      <BreadCrumb addPadding={false} ArrayOfBreadCrumb={ArrayOfBreadCrumb} />
      {deliveryData?.map((data) => (
        <RenderDelivery key={data?.id} data={data} />
      ))}
    </div>
  );
};

export default FastDelivery;

export async function getServerSideProps({ locale }) {
  const deliveryData = [
    {
      id: 1,
      title: "Fast Delivery",
      title_ar: "توصيل سريع",
      list: [
        {
          id: 101,
          name: "Delivery Fee",
          name_ar: "رسوم التوصيل",
          type: fastDeliveryDisplayTypes.KEY_VALUE,
          childrens: [
            {
              id: 1001,
              text: "Small products: SAR<strong> 30</strong>",
              text_ar: "المنتجات الصغيرة: <strong>30 SAR</strong>",
            },
            {
              id: 1002,
              text: "Electronics: SAR<strong> 30</strong> for orders <= SAR<strong> 299</strong> <br/> <strong>free</strong> for orders > SAR<strong> 299</strong>",
              text_ar:
                "الإلكترونيات: <span>• </span><strong>30 SAR</strong> للطلبات =< <strong>299 SAR</strong> <br/> <span>• </span><strong>مجاني</strong> للطلبات > <strong>299 SAR</strong>",
            },
            {
              id: 1003,
              text: "Large products: SAR<strong> 149</strong> (On selected items)",
              text_ar:
                "المنتجات الكبيرة: <strong>149 SAR</strong> (علي منتجات محددة)",
            },
          ],
          icon: "/imgs/delivery-van.svg",
        },
        {
          id: 102,
          name: "Delivery Time",
          name_ar: "مدة التوصيل",
          type: fastDeliveryDisplayTypes.TEXT,
          childrens: [
            {
              id: 1003,
              text: "Delivered within <strong>1– 4 hours </strong> after location is shared.",
              text_ar:
                "المنتجات الصغيرة تم توصيل الطلب خلال <strong>1- 4 ساعات</strong> بعد مشاركة الموقع.",
            },
            {
              id: 1004,
              text: "Large products are delivered the <strong>same day</strong>.",
              text_ar:
                "المنتجات الكبيرة يتم توصيلها في <strong>نفس اليوم</strong>.",
            },
            {
              id: 1005,
              text: "Orders must be placed <strong>before 4:00 PM</strong> for same day delivery.",
              text_ar:
                "يجب إتمام الطلب قبل الساعة<strong> 4:00 مساءً</strong> للحصول على خدمة التوصيل في نفس اليوم.",
            },
            {
              id: 1008,
              text: "The service is available every day of the week except Friday.",
              text_ar:
                "الخدمة متاحة طوال أيام الأسبوع عدا يوم الجمعة.",
            },
          ],
          icon: "/imgs/DeliveryTimeIcon.svg",
        },
        {
          id: 103,
          name: "Applicable for",
          name_ar: "الخدمة متاحة",
          type: fastDeliveryDisplayTypes.TEXT,
          childrens: [
            {
              id: 1006,
              text: "<strong>Small products</strong> and selected products only (Clearly marked at checkout).",
              text_ar:
                "<strong>للمنتجات الصغيرة</strong> ومنتجات كبيرة محددة (يتم تمييزها عند إتمام الشراء).",
            },
            {
              id: 1007,
              text: "Service available only in <strong>eligible locations</strong> (confirmed during checkout).",
              text_ar:
                "التوصيل متاح فقط في <strong>المواقع المؤهلة</strong> (يتم التأكيد عليها عند إتمام الشراء).",
            },
          ],
          icon: "/imgs/ApplicableForIcon.svg",
        },
      ],
      notes: {
        title: "Important Note:",
        title_ar: "ملاحظة هامة:",
        list: [
          "Delivery times may be extended during promotions or high-demand periods",
          "Fee is non-refundable if delay is due to external reasons (e.g. weather conditions, customer unavailability, or incorrect address).",
        ],
        list_ar: [
          "قد يتم تمديد أوقات التوصيل خلال العروض الترويجية أو فترات الذروة",
          "الرسوم غير قابلة للاسترداد إذا كان التأخير بسبب أسباب خارجية (مثل الطقس ، وعدم توفر العميل ، وما إلى ذلك).",
          "في حال تم إلغاء الطلب بعد شحنه، لا يمكن استرجاع رسوم التوصيل",
        ],
      },
      mainImg: "/imgs/FastDeliveryImg.svg",
    },

    {
      id: 2,
      title: "Standard Delivery",
      title_ar: "توصيل عادي",
      list: [
        {
          id: 201,
          name: "Delivery Fee",
          name_ar: "رسوم التوصيل",
          type: fastDeliveryDisplayTypes.KEY_VALUE,
          childrens: [
            {
              id: 2001,
              text: "Small products: SAR<strong> 15</strong> for orders <= SAR<strong> 299</strong> <br/> <strong>free</strong> for orders > SAR<strong> 299</strong>",
              text_ar:
                "المنتجات الصغيرة: <span>• </span><strong>15 SAR</strong> للطلبات =< <strong>299 SAR</strong> <br/> <span>• </span><strong> مجاني</strong> للطلبات > <strong>299 SAR</strong>",
            },
            {
              id: 2002,
              text: "Electronics: SAR<strong> 15</strong> for orders ≤ SAR<strong> 299</strong> <br/> <strong>Free shipping</strong> for orders > SAR<strong> 299</strong>",
              text_ar:
                "الإلكترونيات: <span>• </span><strong>15</strong> SAR للطلبات التي قيمتها أقل من أو تساوي <strong>299</strong> SAR <br/><span>• </span><strong>شحن مجاني</strong> للطلبات التي تزيد عن <strong>299</strong> SAR",
            },
            {
              id: 2003,
              text: "Large products: SAR<strong> 59</strong>",
              text_ar: "المنتجات الكبيرة: <strong>59</strong> SAR",
            },
          ],
          icon: "/imgs/delivery-van.svg",
        },
        {
          id: 202,
          name: "Delivery Time",
          name_ar: "مدة التوصيل",
          type: fastDeliveryDisplayTypes.TEXT,
          childrens: [
            {
              id: 2002,
              text: "Main cities: <strong>1–3</strong> Business days.",
              text_ar: "المدن الرئيسية: <strong>1–3 أيام عمل</strong>",
            },
            {
              id: 2003,
              text: "Other cities: <strong>Up to 5</strong> Business days.",
              text_ar: "المدن الأخرى: حتى<strong> 5 أيام عمل</strong>",
            },
          ],
          icon: "/imgs/DeliveryTimeIcon.svg",
        },
        {
          id: 203,
          name: "Applicable for",
          name_ar: "يطبق على",
          type: fastDeliveryDisplayTypes.TEXT,
          childrens: [
            {
              id: 2004,
              text: "All products (Small & Large)",
              text_ar: "جميع المنتجات (الصغيرة والكبيرة)",
            },
          ],
          icon: "/imgs/ApplicableForIcon.svg",
        },
        {
          id: 204,
          name: "Additional Fee",
          name_ar: "رسوم إضافية",
          type: fastDeliveryDisplayTypes.TEXT,
          childrens: [
            {
              id: 2005,
              text: "An additional <strong>SAR 50</strong> applies for large item deliveries above <strong>2nd</strong> floor.",
              text_ar:
                "لتوصيل المنتجات الكبيرة الي الطوابق الاعلي من الطابق الثاني <strong>50 SAR</strong>",
            },
          ],
          icon: "/imgs/AdditionalFeeIcon.svg",
        },
      ],
      notes: {
        title: "Important Note:",
        title_ar: "ملاحظة هامة:",
        list: [
          "Delivery times may be longer during promotions or high-demand periods",
        ],
        list_ar: [
          "قد يتم تمديد أوقات التوصيل خلال العروض الترويجية أو فترات الذروة.",
        ],
      },
      mainImg: "/imgs/StandardDeliveryImg.svg",
    },

    {
      id: 3,
      title: "Pickup From Store",
      title_ar: "استلام من المتجر",
      list: [
        {
          id: 301,
          name: "Pickup Fee",
          name_ar: "رسوم الاستلام",
          type: fastDeliveryDisplayTypes.TEXT,
          childrens: [
            {
              id: 3001,
              text: "<strong>Free</strong>",
              text_ar: "<strong>مجاناً</strong>",
            },
          ],
          icon: "/imgs/delivery-van.svg",
        },
        {
          id: 302,
          name: "Pickup Time",
          name_ar: "وقت الاستلام",
          type: fastDeliveryDisplayTypes.TEXT,
          childrens: [
            {
              id: 3002,
              text: "Orders will be ready for pickup within <strong>45 minutes</strong> from order placement",
              text_ar:
                "ستكون الطلبات جاهزة للاستلام في غضون <strong>45 دقيقة</strong> من إتمام الطلب",
            },
          ],
          icon: "/imgs/DeliveryTimeIcon.svg",
        },
        {
          id: 303,
          name: "Applicable for",
          name_ar: "يطبق على",
          type: fastDeliveryDisplayTypes.TEXT,
          childrens: [
            {
              id: 3003,
              text: "Available for any items that have the “Available at Stores” option at product page",
              text_ar:
                "متاح للمنتجات التي تحتوي على خيار “متوفر في المعارض” في صفحة المنتج",
            },
          ],
          icon: "/imgs/ApplicableForIcon.svg",
        },
      ],
      notes: {
        title: "Important Note:",
        title_ar: "ملاحظة:",
        list: [
          "Orders must be collected within 3 days of confirmation, or they may be canceled",
        ],
        list_ar: [
          "يجب استلام الطلبات في غضون 3 أيام من التأكيد، أو قد يتم إلغاؤها",
        ],
      },
      mainImg: "/imgs/PickupFromStoreImg.svg",
    },
  ];
  const metadata = {
    ar: {
      title: "خدمة التوصيل من المنيع - الرسوم ومدة التوصيل",
      description:
        "تعرف على رسوم التوصيل والمدة المقدرة لتوصيل مختلف المنتجات عند شرائها من المنيع. اختر من خدمة التوصيل السريع، العادي، أو استلم مشترياتك من المتجر.",
    },
    en: {
      title: "Almanea Delivery Service - Fees and Delivery Time",
      description:
        "Delivery fees and estimated delivery times for various products when purchasing from Almanea. Choose from express delivery, regular delivery, or in-store pickup.",
    },
  };
  return {
    props: {
      locale,
      metadata: metadata[locale],
      deliveryData,
      ...(await serverSideTranslations(locale, ["common", "blogs"])),
    },
  };
}
